import { Box } from '@mantine/core';
import type { CSSObject } from 'styled-components';
import styled from 'styled-components';
import {
	DS_AI_ELEVATION,
	DS_AI_MOTION,
	dsAiFocusRingStyles,
} from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';
import { dsAiTypeStyle } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.typography';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import type { CellAlign, CellTrack } from './DataGrid.types';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';

const { colors } = defaultTheme.v3;

/** Set on a table frame while its content scrolls under the right-pinned columns. */
const UNDER_PIN = '[data-under-pin]';

/** The same, for the left-pinned columns. */
const PAST_PIN = '[data-past-pin]';

/**
 * A cell's width in a flex row: growing above a floor, or fixed. A pinned cell also sticks to the scroller's right
 * edge, with the edge shade drawn while content scrolls under it. Sticky insets count from the scroller's content
 * box, so the body's padding and the frame's border are cancelled; else cells scroll through that strip.
 */
export const dsAiTrackStyles = ({ min, width, pinRight, pinLeft, shade }: CellTrack) =>
	({
		...(width === undefined
			? { flex: '1 1 0', minWidth: `${min ?? 0}px` }
			: { flex: `0 0 ${width}px` }),
		...(pinRight !== undefined && {
			position: 'sticky',
			right: `calc(${pinRight} + 1px - 16px - 1px)`,
		}),
		...(pinLeft !== undefined && {
			position: 'sticky',
			left: `calc(${pinLeft} + 1px - 16px - 1px)`,
		}),
		...((pinRight ?? pinLeft) !== undefined && {
			zIndex: 1,
			alignSelf: 'stretch',
			background: 'inherit',
		}),
		...(shade && {
			[`${pinRight !== undefined ? UNDER_PIN : PAST_PIN} &`]: {
				boxShadow:
					pinRight !== undefined ? DS_AI_ELEVATION.shadeRight : DS_AI_ELEVATION.shadeLeft,
			},
		}),
	}) satisfies CSSObject;

export interface GridRowOptions {
	/** A grid row's columns; without it the row is a flex row whose cells carry a `track`. */
	template?: string;
	muted?: boolean;
	tinted?: boolean;
	lightDivider?: boolean;
}

/** Discarded rows read as grey, the row being resolved as tinted, everything else as plain. */
const rowBackground = ({ muted, tinted }: { muted?: boolean; tinted?: boolean }) => {
	if (muted === true) {
		return colors.gray[2];
	}
	return tinted === true ? colors.blue[0] : colors.white[0];
};

/** A table row: its columns, its divider and the fade a discarded row takes. */
export const dsAiGridRowStyles = ({ template, muted, tinted, lightDivider }: GridRowOptions = {}) =>
	({
		...(template === undefined
			? { display: 'flex' }
			: { display: 'grid', gridTemplateColumns: template }),
		alignItems: 'center',
		minHeight: '48px',
		borderBottom: `1px solid ${lightDivider ? colors.gray[2] : colors.gray[4]}`,
		background: rowBackground({ muted, tinted }),
		color: colors.dark[9],
		// Muted (Discarded) rows fade as a whole, status chip included (Figma: cells at 50%).
		opacity: muted ? 0.5 : 1,
		transition: `background-color ${DS_AI_MOTION.fastMs}ms ${DS_AI_MOTION.easing}, opacity ${DS_AI_MOTION.statusChangeMs}ms ${DS_AI_MOTION.easing}`,
		...dsAiTypeStyle('sans/14/400'),
		'&:last-child': { borderBottom: 'none' },
	}) satisfies CSSObject;

/** Spread over {@link dsAiGridRowStyles}; its `:last-child` deliberately overrides the row's. */
export const DSAI_HEADER_ROW_STYLES = {
	...dsAiTypeStyle('sans/14/600'),
	color: colors.dark[9],
	background: colors.white[0],
	'&:last-child': { borderBottom: `1px solid ${colors.gray[4]}` },
} satisfies CSSObject;

export interface CellOptions {
	align?: CellAlign;
	/** Lays children out in a row without clipping, for cells holding controls. */
	flex?: boolean;
	track?: CellTrack;
}

export const dsAiCellStyles = ({ align = 'start', flex, track }: CellOptions = {}) =>
	({
		minWidth: 0,
		padding: '0 12px',
		textAlign: align,
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
		...(flex && {
			display: 'flex',
			alignItems: 'center',
			justifyContent: align,
			gap: '12px',
			overflow: 'visible',
		}),
		...(track && dsAiTrackStyles(track)),
		...((track?.pinRight ?? track?.pinLeft) !== undefined && {
			display: 'flex',
			alignItems: 'center',
			justifyContent: align,
			overflow: 'visible',
		}),
	}) satisfies CSSObject;

export const dsAiIconCellStyles = (track?: CellTrack) =>
	({
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		...(track && dsAiTrackStyles(track)),
	}) satisfies CSSObject;

/** Pinned to the visible part of a table wider than its panel. */
export const PINNED_TO_VISIBLE_WIDTH_STYLES = {
	position: 'sticky',
	left: '1px',
	boxSizing: 'border-box',
	width: 'calc(100cqw - 2px)',
} satisfies CSSObject;

/** Pinned to the visible part of a table wider than its panel. */
export const PinnedToVisibleWidth = styled(Box)<StyledMantineProps>(PINNED_TO_VISIBLE_WIDTH_STYLES);

/** "Show {panel}" after the chip while the middle panel is folded away (design `.link.t12.sb-500`). */
export const DSAI_FILTER_ROW_LINK_STYLES = {
	flexShrink: 0,
	padding: 0,
	border: 'none',
	background: 'none',
	color: colors.blue[6],
	whiteSpace: 'nowrap',
	cursor: 'pointer',
	...dsAiTypeStyle('sans/12/500'),
	'&:hover': { textDecoration: 'underline' },
	...dsAiFocusRingStyles(1),
} satisfies CSSObject;

/** The main panel body's container name: its width is the visible width of the table it holds. */
export const TABLE_CONTAINER = 'dsai-table';

/** The table width below which the toolbar's Filter and Columns show their icons only (design `.tbl-box` 700px). */
export const COMPACT_TOOLBAR_QUERY = `${TABLE_CONTAINER} (max-width: 700px)`;

/** Set on a toolbar whose Filter and Columns labels give way in a narrow table. */
export const COMPACT_LABELS = '[data-compact-labels]';

/** The table width below which the quick filter gives up its minimum width (design `.tb-select`, 760px). */
export const NARROW_TOOLBAR_QUERY = `${TABLE_CONTAINER} (max-width: 760px)`;

/**
 * A toolbar button's label (design `.tb-l`): in a narrow table only the icon shows, and the label stays the button's
 * accessible name. Only in a toolbar marked `COMPACT_LABELS`.
 */
export const COMPACT_TOOL_LABEL_STYLES = {
	[`@container ${COMPACT_TOOLBAR_QUERY}`]: {
		[`${COMPACT_LABELS} &`]: {
			position: 'absolute',
			width: '1px',
			height: '1px',
			overflow: 'hidden',
			clip: 'rect(0 0 0 0)',
			whiteSpace: 'nowrap',
		},
	},
} satisfies CSSObject;

/** A toolbar label that collapses away in the narrow toolbar. */
export const CompactToolLabel = styled(Box)<StyledMantineProps>(COMPACT_TOOL_LABEL_STYLES);

/** Left-bordered toolbar button cell. Its label child carries `data-tool-label`. */
export const dsAiToolCellStyles = (width?: number) =>
	({
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: width === 48 ? 'center' : 'flex-start',
		gap: '8px',
		flexShrink: 0,
		width: width ? `${width}px` : 'auto',
		padding: '0 12px',
		border: 'none',
		borderLeft: `1px solid ${colors.gray[2]}`,
		background: 'transparent',
		color: 'inherit',
		font: 'inherit',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		'&:disabled': { cursor: 'not-allowed', color: colors.gray[6], background: 'transparent' },
		'&:first-child': { borderLeft: 'none' },
		'&:hover:not(:disabled)': { background: colors.gray[2] },
		[`@container ${COMPACT_TOOLBAR_QUERY}`]: {
			[`${COMPACT_LABELS} &:has(> [data-tool-label])`]: { gap: 0 },
		},
		...dsAiFocusRingStyles(-2),
	}) satisfies CSSObject;

/** A toolbar slot holding the Filter, Columns or quick-filter control. */
export const toolbarSlotCellStyles = (compact: boolean) =>
	({
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
		// Inside the class, not a `miw` prop: a style prop is inline and would outrank the @container override.
		minWidth: compact ? 0 : 141,
		padding: compact ? 0 : 4,
		borderLeft: `1px solid ${colors.gray[2]}`,
		[`@container ${NARROW_TOOLBAR_QUERY}`]: { minWidth: 0 },
	}) satisfies CSSObject;

/** A grid row: the shared row frame, with the muted / tinted / light-divider variants as props. */
export const DsAiGridRow = styled(Box)<
	StyledMantineProps & {
		$template?: string;
		$muted?: boolean;
		$tinted?: boolean;
		$lightDivider?: boolean;
	}
>(({ $template, $muted, $tinted, $lightDivider }) =>
	dsAiGridRowStyles({
		template: $template,
		muted: $muted,
		tinted: $tinted,
		lightDivider: $lightDivider,
	})
);

/** A cell in a grid row; `$track` sizes and pins it, `$flex` turns it into a row of its own. */
export const DsAiCell = styled(Box)<
	StyledMantineProps & {
		$align?: CellAlign;
		$flex?: boolean;
		$track?: CellTrack;
	}
>(({ $align, $flex, $track }) => dsAiCellStyles({ align: $align, flex: $flex, track: $track }));

/** A cell holding only an icon or a control. */
export const DsAiIconCell = styled(Box)<StyledMantineProps & { $track?: CellTrack }>(({ $track }) =>
	dsAiIconCellStyles($track)
);

/** A toolbar slot holding the Filter, Columns or quick-filter control. */
export const ToolbarSlotCell = styled(Box)<StyledMantineProps & { $compact?: boolean }>(
	({ $compact }) => toolbarSlotCellStyles($compact === true)
);
